import { shouldUseKana } from '@/utils/i18n'
import {
  STATUS_REGISTERED,
  STATUS_ENABLED,
  STATUS_STOPPED,
  STATUS_PENDING_REVIEW,
  STATUS_REJECTED,
  STATUS_WITHDRAWN,
  PAYMENT_METHOD_INVOICE,
} from '@/consts/lov'
import { isMonthlyPlan, isOnetimePlan } from '@/utils/planUtils'
import Vue from 'vue'

export function convertUserDataForSave(userState) {
  // kanaは必須項目なので、利用しない場合でもnameをそのままコピーする
  if (!shouldUseKana()) {
    userState.kana1 = userState.name1
    userState.kana2 = userState.name2
    userState.company_name_kana = userState.company_name
    userState.company_ceo_name_kana1 = userState.company_ceo_name1
    userState.company_ceo_name_kana2 = userState.company_ceo_name2
  }

  // 個人ユーザーの場合に、集配先住所名称を自動で付加し、集配先住所を請求先住所にコピーする.
  // 個人/法人フラグをつける前のデータではbill_address系に集配先住所が入っていたが、個人/法人フラグ
  // をつける際に一旦bill_address系をsend_address系にコピーして、以後はsend_address系を集配先住所として取り扱うようにした.
  // しかしコード内で集配先住所としてbill_addressを見ている箇所を先に全部つぶすのが困難と思われたため、
  // とりあえずsend_addressをbill_addressにコピーしておけば困ったことにはならないだろうということでこの処理を入れた.
  if (userState.is_company) return

  // パートナー会社経由の場合は、send_addr_detail3に部屋番号を付加する
  if (!!userState.source_venue_code) {
    const buildingName = userState.send_addr_detail3.trim().split('@')[0]
    userState.send_addr_detail3 = `${buildingName} @${userState.send_addr_detail3_room_number}`
  }

  userState.send_addr_name = '住所1' // 何か入れる
  userState.bill_addr_zipcode = userState.send_addr_zipcode
  userState.bill_addr_detail1 = userState.send_addr_detail1
  userState.bill_addr_detail2 = userState.send_addr_detail2
  userState.bill_addr_detail3 = userState.send_addr_detail3
}

export function getValidPaymentMethodKeys(isCompany) {
  // 法人 => クレカ、請求書
  // 個人 => クレカ
  return isCompany ? ['1', '2'] : ['1']
}

export function getValidOneTimePaymentMethodKeys(isCompany) {
  // 法人 => クレカ、Paypay, 請求書(Paid)
  // 個人 => クレカ, Paypay
  return isCompany ? ['1', '2', '3'] : ['1', '2']
}

const statusTransitionMap = {
  [STATUS_REGISTERED]: [STATUS_ENABLED, STATUS_PENDING_REVIEW], // 登録済 -> 利用中, 審査中
  [STATUS_ENABLED]: [STATUS_STOPPED, STATUS_WITHDRAWN], // 利用中 -> 一時停止, 退会済
  [STATUS_STOPPED]: [STATUS_ENABLED, STATUS_WITHDRAWN], // 一時停止 -> 利用中, 退会済
  [STATUS_PENDING_REVIEW]: [STATUS_ENABLED, STATUS_REJECTED], // 審査中 -> 利用中, 審査否決
  [STATUS_REJECTED]: [STATUS_ENABLED, STATUS_STOPPED, STATUS_WITHDRAWN], // 審査否決 -> 利用中, 一時停止, 退会済
  [STATUS_WITHDRAWN]: [STATUS_ENABLED, STATUS_STOPPED] // 退会済 -> 利用中, 一時停止
}

export function getAvailableStatusOptions(user, origStatusOptions) {
  // 現在のステータス + 次に遷移可能なステータス
  let possibleStatusKeys = [parseInt(user.status), ...(statusTransitionMap[user.status] || [])]
  // PENDING_REVIEWとREJECTEDは支払い方法が請求書払いの場合のみ選択可能
  if (user.payment_method_id !== PAYMENT_METHOD_INVOICE) {
    possibleStatusKeys = possibleStatusKeys.filter(k =>
      k !== STATUS_PENDING_REVIEW &&
      k !== STATUS_REJECTED
    )
  }

  return origStatusOptions.filter(lovStatus =>
    possibleStatusKeys.includes(parseInt(lovStatus.key))
  )
}

export function isUserCreatedThisMonth(user) {
  const dtFormat = Vue.filter('dtFormat')
  const uStartMonth = dtFormat(user.created_at, 'yyyymm')
  const thisMonth = dtFormat(new Date(), 'yyyymm')
  return thisMonth === uStartMonth
}

export function userPlanValidAt(userPlans, date) {
  return userPlans.find(userPlan => {
    const validFrom = new Date(userPlan.valid_from)
    const validUntil = new Date(userPlan.valid_until)
    if (Number.isNaN(validFrom.getTime()) || Number.isNaN(validUntil.getTime())) return false

    return validFrom.getTime() <= date.getTime() && validUntil.getTime() > date.getTime()
  }) || null
}

export function getNextPlanFromUserPlans(userPlans, currentPlanId, currentPlanOption) {
  if (userPlans.length === 0) return null

  const now = new Date()
  const nextMonthStart = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0, 0)
  const planAtNextMonthStart = userPlanValidAt(userPlans, nextMonthStart)
  if (!planAtNextMonthStart?.plan_id) return null

  const hasPlanChanged =
    planAtNextMonthStart.plan_id !== currentPlanId ||
    planAtNextMonthStart.user_plan_option !== currentPlanOption
  if (!hasPlanChanged) return null

  return planAtNextMonthStart
}

export function replaceUserInList(users, idx, user) {
  if (!Array.isArray(users)) return
  let matchedIndex = idx
  if (matchedIndex === null || matchedIndex === undefined) {
    matchedIndex = users.findIndex((candidate) => candidate && candidate.id === user.id)
  }
  if (matchedIndex === -1) return
  Vue.set(users, matchedIndex, user)
}

export function isOnetimeToMonthlyChangedThisMonth(user) {
  const userPlans = Array.isArray(user?.user_plans) ? user.user_plans : []
  if (userPlans.length === 0) return false

  const now = new Date()
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0)
  const userPlanAtMonthStart = userPlanValidAt(userPlans, monthStart)
  if (!userPlanAtMonthStart) return false

  // 月初時点で単発プラン、かつ現在のプランが月額プランであれば単発→月額変更とする
  return isOnetimePlan(userPlanAtMonthStart?.plan_id) && isMonthlyPlan(user.user_plan.plan_id)
}
